import {
  GALLERY,
  HEADER,
  PATH_EDIT,
  STORIES_PUBLISHER_PRIVILEGE,
  STORIES_EDITOR_PRIVILEGE,
  STORIES_PDF_GENERATION_PRIVILEGE,
  STORIES_ADMIN_PRIVILEGE,
  STORIES_VIEWER_PRIVILEGE,
  COMMENTS_TAB_ID,
  COMMENTS_TAB_TITLE,
  SIDE_PANEL_TABS_SECTION_TITLE,
  TOPIC_TYPE_STORY,
  TAB_PUBLICATIONS,
} from '@opengov/stories-shared/constants';
import {
  IAllProposalDetails,
  IMergeTags,
  INewStoryContent,
  IStory,
  IStoryContent,
  IStoryMeta,
} from '@opengov/stories-shared/types';
import { initializeCustomColors } from '@opengov/stories-shared/utils/general';
import { selectContentGroups, selectNewStoryContents } from '@opengov/stories-shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Alert from 'react-s-alert';

import {
  IEditorMessages,
  IExportStoryProps,
  IModalMessages,
  IPreviewPdfProps,
  ISettings,
  IStoryPrivilege,
  IStoryViewMessages,
  IUpdateCustomColorProps,
  IUpdateStoryContentProps,
  IUpdateStoryProps,
  IExtendedStoryPrivilege
} from '../../../../types';
import {
  checkStoryMergeTags,
  MergeTagsOperation,
  MergeTagsProvider,
  updateStoryMergeTags,
} from '../../../common/context/MergeTagsContext';
import { Flash } from '../../StoriesPage';
import { CurrentEditorProvider } from '../../StoryEditor/CurrentEditorContext';
import { Content } from '../Content';
import { Header } from '../Header';
import { MaxNumberOfMergeTagsBanner } from '../MaxNumberOfMergeTagsBanner';
import { Sidebar } from '../Sidebar';
import { ProgressTrackerSettingsProvider } from '../../StoriesSettings/ProgressTrackerSettings/context/ProgressTracker.Provider';

import { styles } from './StoryView.styles';
import { useFeatureFlagStore } from '@opengov/feature-flag/launchDarkly';
import { CopyStory } from '../../../../modals/components/CopyStory';
import { CopyToTemplate } from '../../../../modals/components/CopyToTemplate';
import { StorySettings } from '../../../../modals/components/StorySettings';
import { ShareVisibilitySettings } from '../../../../modals/components/ShareVisibilitySettings';
import { ApplyBranding } from '../../../../modals/components/ApplyBranding';
import { AddContent } from '../../../../modals/components/AddContent/AddContent';
import { AddEmbed } from '../../../../modals/components/AddEmbed/AddEmbed';
import { AddMedia } from '../../../../modals/components/AddMedia/AddMedia';
import { ClearContent } from '../../../../modals/components/ClearContent';
import { DeleteContent } from '../../../../modals/components/DeleteContent';
import { ResetContent } from '../../../../modals/components/ResetContent';
import { DeleteStory } from '../../../../modals/components/DeleteStory';
import { RepublishStory } from '../../../../modals/components/RepublishStory';
import { ExportPdfOptions } from '../../../../modals/components/ExportPdfOptions/ExportPdfOptions';
import { PdfLoading } from '../../../../modals/components/PdfLoading';
import { PageErrorBoundary } from '../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../instrumentation/views';
import { hasPrivilege, hasSubjectStoryClaim, updateLogo } from '../../../utils';
import { sendStoriesViewedActivity } from '../../../activity/sendStoriesViewedActivity';
import { WorkspaceSidebarProvider } from '@opengov/bnp-component-workflows-widgets';
import { CommentTextOutline } from '@opengov/react-capital-assets';
import { addComment, fetchComments, FunctionArgsType } from '@opengov/stories-shared/utils/comments/commentsUtils';
import { useGetSession } from '@opengov/bnp-core';
import {
  ArgsType,
  CommentsWidget,
  DeleteCommentResponse,
  UserType,
  Comment
} from '@opengov/bnp-component-community-widgets';
import { OpenGovAppConfig } from '@opengov/app-config';
import { transport } from '@opengov/app-core/transport';
import { Box, TabProps, TooltipProps } from '@mui/material';
import { sendCommentRemovedActivity, CommentDeletedActivityData } from '../../../activity/sendCommentRemovedActivity';
import { ModalProvider } from './contexts/ModalProvider';
import { EditorModalProvider } from './contexts/EditorModalContext';
import { useProgressTrackerSettingsContext } from '../../StoriesSettings/ProgressTrackerSettings/context/useProgressTrackerSettingsContext';
import { usePublicationById } from '../../Publications/PublicationView/hooks/usePublicationById';
import { useFetchSettings } from '../../StoriesSettings/hooks/useFetchSettings';
import { EMPTY_SETTINGS } from '../../StoriesSettings/emptySettings';
import { useToast } from '../../Toast/context/useToast.context';
import {
  useCurrentSubjectClaims,
  useSubjectPublicationStoryClaim
} from '../../../hooks/subjectClaims';
import {
  useUpdatePublicationUpdatedAt,
  useSetPublicationThumbnail,
} from '../../Publications/PublicationView/hooks/usePublicationMutations';
import {
  storyViewQueryKeys,
  StoryContentMetaProvider,
  useStory,
  UseStoryParams,
  useStoryContents,
  UseStoryContentsParams,
  useProposalsInStory,
  UseProposalsInStoryParams,
  useStoryContentMeta,
  useUpdateStory,
  usePublishStory,
  useUnpublishStory,
  usePreviewPdf,
  useUpdateStoryCustomColors,
  useUpdateStoryContentDB,
  useUpdateAllStoryContentsDB,
  useUpdateAllStoryContentsMergeTagsDB,
  useRefreshEmbeds,
  useDeleteStoryContent,
  useDeleteStoryEmbed,
  useSaveProgressTrackerConfig,
  useCreateStoryContent,
  useExportStory,
  useSidebarCollapsedState,
  STORY_OVERVIEW_SIDEBAR_STORAGE_KEY_PREFIX,
  STORY_PAGE_LAYOUT_PROPOSALS_SIDEBAR_STORAGE_KEY_PREFIX,
} from '../hooks';
import { getStoryEmbed } from '../../../routes/hasura';

export interface IStoryViewProps {
  canAddProposalContent: boolean;
  isTemplate: boolean;
  isOpenGovAdmin: boolean;
  isEntityGroupPartner: boolean;
  isEntityUserWithEdit: boolean;
  canCopyIndividualStories: boolean;
  canEditPageBreaks: boolean;
  canEditSpacing: boolean;
  canUseAiToGenerateContent: boolean;
  canUseStoriesSummarizerGA: boolean;
  canUseOgAssistGenerateSummary: boolean;
  isAiToggleFeatureEnabled: boolean;
  publicationId?: string; // Story 7: changed from publication object → fetch via hook
  lastPath: string;
  storyId: string;
  entityId: number;
  entityName: string;
  entityUuid: string;
  entitySubdomain: string;
  userId: number;
  userUuid: string;
  settings?: ISettings; // Story 7 Slice 9: no longer supplied by route selector; useFetchSettings is authoritative
  messages: IStoryViewMessages;
  editorMessages: IEditorMessages;
  modalMessages: IModalMessages;
  displayPublicationTab: boolean;
  storiesAccessControlEnabled: boolean;
  isCustomSpreadsheetReportEnabled: boolean;
  isFormsAndWorkflowsFeatureEnabled: boolean;
  isPerformanceManagementFeatureEnabled: boolean;
  openModal(modal: string): IAction<string, string>;
  navigateToStoryList(): any;
  navigateToStoryEdit(): any;
  navigateToStoryView(): any;
  navigateToPublicationView(): any;
  navigateToPublicationList(): any;
  navigateToPublicationEdit(storyId: string, isTemplate: boolean, publicationId: string): any;
  showProgressTracker: boolean;
  navigateToStorySetupPDF(storyId: string): void;
  navigateToPublicationStorySetupPDF(storyId: string, publicationId: string): void;
  isStorySetupPdfEnabled: boolean;
}
export type WorkspaceSidebarTab = {
  id: string;
  tooltip?: TooltipProps['title'];
  panelTitle: string;
  panelSubtitle?: string;
  panelContent: React.ReactNode;
  panelActions?: React.ReactNode;
} & Pick<TabProps, 'icon'>;

export type WorkspaceSidebarTabsSection = {
  title: string;
  tabs: WorkspaceSidebarTab[];
};
const StoryViewInternal = (props: IStoryViewProps) => {
  const {
    canAddProposalContent,
    editorMessages,
    entityId,
    entitySubdomain,
    isTemplate,
    lastPath,
    messages,
    storyId,
    userId,
    userUuid,
    storiesAccessControlEnabled,
    publicationId,
    entityName,
    entityUuid,
    isFormsAndWorkflowsFeatureEnabled,
    isPerformanceManagementFeatureEnabled,
  } = props;

  const { toggleToast } = useToast();

  // Fetch publication via hook when publicationId exists (replaces
  // route onEnter prefetch into compose state)
  const publicationQuery = usePublicationById(
    publicationId && entityId > 0 ? { publicationId } : null,
    editorMessages.somethingWentWrongError
  );
  const publication = publicationQuery.data ?? null;

  const { data: currentSubjectClaims } = useCurrentSubjectClaims(
    entityUuid && userUuid ? { userUuid, entityUuid } : null
  );
  const publicationStoryClaimQuery = useSubjectPublicationStoryClaim(
    publicationId && userUuid && entityUuid
      ? {
        publicationId,
        subjectId: userUuid,
        storyId,
        entityUuid,
        storiesAccessControlEnabled
      }
      : null
  );
  const currentSubjectPublicationStoryClaim = publicationStoryClaimQuery.data?.allClaims ?? null;
  // For a publication story, `canView`/`canEdit` are derived from the
  // publication-story claim, which loads in a SEPARATE query from the
  // entity-level `currentSubjectClaims`. The access-guard redirect below must
  // wait for this query to settle — otherwise a non-admin (e.g. reviewer)
  // gets redirected out during the window where entity claims have resolved
  // but the story-level claim has not yet arrived.
  const publicationStoryClaimSettled =
    !publicationId || publicationStoryClaimQuery.isSuccess || publicationStoryClaimQuery.isError;

  // Fetch settings reactively so branding colors are always populated.
  // react-query caches per entityId, so a warm cache returns instantly; on a
  // cold load `fetchedSettings` is briefly undefined and we render EMPTY_SETTINGS
  // until the fetch resolves, then re-render with real branding.
  const { data: fetchedSettings, isLoading: isSettingsLoading } = useFetchSettings({ entityId });
  const settings = fetchedSettings ?? props.settings ?? EMPTY_SETTINGS;

  const isReadOnly = lastPath !== PATH_EDIT;

  // --- Compose-app state reads replaced with TanStack Query hooks ---
  const queryClient = useQueryClient();

  const storyQueryParams = useMemo(
    (): UseStoryParams => (entityId > 0 ? { storyId, isTemplate } : null),
    [entityId, storyId, isTemplate]
  );
  const storyContentsQueryParams = useMemo(
    (): UseStoryContentsParams => (entityId > 0 ? { storyId, entityId, isTemplate } : null),
    [entityId, storyId, isTemplate]
  );
  const proposalsQueryParams = useMemo(
    (): UseProposalsInStoryParams =>
      canAddProposalContent && entityId > 0 ? { storyId, entityId } : null,
    [canAddProposalContent, entityId, storyId]
  );

  const storyQuery = useStory(storyQueryParams, editorMessages.somethingWentWrongError);
  const storyContentsQuery = useStoryContents(storyContentsQueryParams, editorMessages.somethingWentWrongError);
  const proposalsQuery = useProposalsInStory(proposalsQueryParams, editorMessages.somethingWentWrongError);

  const {
    storyContent: contextStoryContent,
    storyContentOrder: contextStoryContentOrder,
    storyContentInView: contextStoryContentInView,
    setStoryContent,
    setStoryContentOrder,
    setStoryContentInView,
  } = useStoryContentMeta();

  const story = storyQuery.data ?? ({} as IStory);
  const rawStoryContents = storyContentsQuery.data ?? [];
  const isLoading = storyQuery.isLoading;
  const isLoadingProposals = proposalsQuery.isLoading;

  // Embeds are not selected by the story query; load them separately and merge
  // into the story cache so the "Refresh Embeds" action gates correctly on load.
  // Mirrors the old storyViewEffects.checkEmbeds; interim until embeds ownership
  // moves to the dedicated embeds migration.
  useEffect(() => {
    if (isTemplate || !storyQueryParams) return;
    if (!storyQuery.data?._id || storyQuery.data.embeds !== undefined) return;
    let cancelled = false;
    void getStoryEmbed(storyId)
      .then((storyEmbeds: Array<{ embedId: string }>) => {
        if (cancelled) return;
        const embeds = (storyEmbeds ?? []).map((e) => e.embedId);
        queryClient.setQueryData<IStory>(storyViewQueryKeys.story(storyQueryParams), (prev) =>
          prev ? { ...prev, embeds } : prev
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [storyQuery.data, storyQueryParams, isTemplate, storyId, queryClient]);

  // Apply merge-tag placeholders in edit mode (matches old storyViewRoute.ts onEnter logic)
  const storyContents = useMemo(() => {
    if (!isReadOnly && canAddProposalContent && proposalsQuery.data?.mergeTags) {
      return updateStoryMergeTags(rawStoryContents, {
        operation: MergeTagsOperation.Update,
        mergeTags: proposalsQuery.data.mergeTags,
      });
    }
    return rawStoryContents;
  }, [rawStoryContents, proposalsQuery.data?.mergeTags, isReadOnly, canAddProposalContent]);

  const hasGallery = useMemo(
    () => storyContents.some((sc) => sc.group === GALLERY),
    [storyContents]
  );

  const storyBudget = proposalsQuery.data?.storyBudget ?? null;
  const proposalFieldsDetails = proposalsQuery.data?.proposalFieldsDetails ?? ({} as IAllProposalDetails);
  const mergeTags = proposalsQuery.data?.mergeTags ?? ({} as IMergeTags);

  const inactiveStoryMergeTagsType = useMemo(
    () =>
      checkStoryMergeTags(
        storyContents,
        proposalsQuery.data?.storyBudget ?? null,
        proposalsQuery.data?.mergeTags ?? {}
      ),
    [storyContents, proposalsQuery.data]
  );

  const storyMeta: IStoryMeta = {
    storyContent: contextStoryContent,
    storyContentOrder: contextStoryContentOrder,
    storyContentInView: contextStoryContentInView,
  };

  // Cache-write callbacks replacing compose-app action dispatches (Rule 4)
  const updateStoryContent = useCallback(
    (storyContent: IStoryContent | INewStoryContent) => {
      if (!storyContentsQueryParams) return;
      const typed = storyContent as IStoryContent;
      queryClient.setQueryData(
        storyViewQueryKeys.storyContents(storyContentsQueryParams),
        (old: IStoryContent[] | undefined) =>
          old?.map((sc) => (sc._id === typed._id ? typed : sc)) ?? []
      );
    },
    [queryClient, storyContentsQueryParams]
  );

  const updateStoryContents = useCallback(
    (newContents: IStoryContent[]) => {
      if (!storyContentsQueryParams) return;
      queryClient.setQueryData(
        storyViewQueryKeys.storyContents(storyContentsQueryParams),
        newContents
      );
    },
    [queryClient, storyContentsQueryParams]
  );

  // Context-write callbacks replacing compose-app meta state dispatches (Rule 5)
  const updateMetaStoryContent = useCallback(
    (storyContent: IStoryContent) => setStoryContent(storyContent),
    [setStoryContent]
  );
  const updateMetaStoryContentOrder = useCallback(
    (order: number) => setStoryContentOrder(order),
    [setStoryContentOrder]
  );
  const updateMetaStoryContentInView = useCallback(
    (id: string) => setStoryContentInView(id),
    [setStoryContentInView]
  );

  // --- Publication-write bridges now use hooks instead of compose-app effects ---
  const { mutateAsync: doUpdatePublicationUpdatedAt } = useUpdatePublicationUpdatedAt();
  const { mutate: doSetPublicationThumbnail } = useSetPublicationThumbnail();

  const updatePublicationUpdatedAt = useCallback(
    async ({ publicationId, userId: userOgId }: { publicationId: string; userId: number }): Promise<void> => {
      await doUpdatePublicationUpdatedAt({ publicationId, userId: userOgId });
    },
    [doUpdatePublicationUpdatedAt]
  );

  const updateThumbnail = useCallback(
    (publicationId: string) => {
      doSetPublicationThumbnail({ publicationId, entityId, entitySubdomain: props.entitySubdomain });
    },
    [doSetPublicationThumbnail, entityId, props.entitySubdomain]
  );

  // --- Story-write operations now use hooks instead of compose-app effects ---
  const { mutate: doUpdateStory, isLoading: isUpdatingStory } = useUpdateStory();
  const { mutate: doPublishStory, isLoading: isPublishing } = usePublishStory();
  const { mutate: doUnpublishStory } = useUnpublishStory();
  const { mutateAsync: doPreviewPdf, isLoading: isPreviewingPdf, isError: isPreviewingPdfFailure } = usePreviewPdf();
  const { mutate: doExportStory, isLoading: isExporting, isError: isExportingFailure } = useExportStory();
  const { mutate: doUpdateStoryCustomColors } = useUpdateStoryCustomColors();
  const { mutate: doUpdateStoryContentDB, isLoading: isSavingStoryContent } = useUpdateStoryContentDB();
  const { mutate: doUpdateAllStoryContentsDB } = useUpdateAllStoryContentsDB();
  const { mutateAsync: doUpdateAllStoryContentsMergeTagsDB } = useUpdateAllStoryContentsMergeTagsDB();
  const { mutate: doRefreshEmbeds, isLoading: isRefreshingEmbeds } = useRefreshEmbeds();
  const { mutate: doDeleteStoryContent } = useDeleteStoryContent();
  const { mutateAsync: doDeleteStoryEmbed } = useDeleteStoryEmbed();
  const { mutateAsync: doSaveProgressTrackerConfig } = useSaveProgressTrackerConfig();
  const { mutateAsync: doCreateStoryContent, isLoading: isCreatingStoryContent } = useCreateStoryContent();

  const isSaving =
    isUpdatingStory || isSavingStoryContent || isRefreshingEmbeds || isCreatingStoryContent;

  const updateStory = useCallback(
    (p: IUpdateStoryProps) => { doUpdateStory(p); },
    [doUpdateStory]
  );
  const publishStory = useCallback(
    (p: Parameters<typeof doPublishStory>[0]) => { doPublishStory(p); },
    [doPublishStory]
  );
  const previewPdf = useCallback(
    (p: IPreviewPdfProps): Promise<string> => doPreviewPdf(p),
    [doPreviewPdf]
  );
  const updateStoryCustomColors = useCallback(
    (p: IUpdateCustomColorProps) => { doUpdateStoryCustomColors(p); },
    [doUpdateStoryCustomColors]
  );
  const updateStoryContentDB = useCallback(
    (p: IUpdateStoryContentProps) => { doUpdateStoryContentDB(p); },
    [doUpdateStoryContentDB]
  );
  const updateAllStoryContentsDB = useCallback(
    (p: Parameters<typeof doUpdateAllStoryContentsDB>[0]) => { doUpdateAllStoryContentsDB(p); },
    [doUpdateAllStoryContentsDB]
  );
  const updateAllStoryContentsMergeTagsDB = useCallback(
    (p: Parameters<typeof doUpdateAllStoryContentsMergeTagsDB>[0]): Promise<void> =>
      doUpdateAllStoryContentsMergeTagsDB(p).then(() => undefined),
    [doUpdateAllStoryContentsMergeTagsDB]
  );
  const refreshEmbeds = useCallback(
    (p: Parameters<typeof doRefreshEmbeds>[0]) => { doRefreshEmbeds(p); },
    [doRefreshEmbeds]
  );
  const deleteStoryEmbed = useCallback(
    (p: Parameters<typeof doDeleteStoryEmbed>[0]): Promise<void> =>
      doDeleteStoryEmbed(p).then(() => undefined),
    [doDeleteStoryEmbed]
  );
  const saveProgressTrackerConfig = useCallback(
    (p: Omit<Parameters<typeof doSaveProgressTrackerConfig>[0], 'entityId'>): Promise<void> =>
      doSaveProgressTrackerConfig({ ...p, entityId }).then(() => undefined),
    [doSaveProgressTrackerConfig, entityId]
  );

  const { featureFlags, getFlags } = useFeatureFlagStore();
  const storyPaddingEnabled = featureFlags.get('product-stories-feature-story-padding') as boolean;
  const storyMarginsEnabled = featureFlags.get('product-stories-feature-story-margins') as boolean;
  const canFollowStories = featureFlags.get('product-budget-feature-story-subscription') as boolean;

  const { progressTrackerSettings } = useProgressTrackerSettingsContext();
  const [isRepublishOpen, setIsRepublishOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCopyStoryOpen, setIsCopyStoryOpen] = useState(false);
  const [isCopyToTemplateOpen, setIsCopyToTemplateOpen] = useState(false);
  const [isStorySettingsOpen, setIsStorySettingsOpen] = useState(false);
  const [isShareVisibilityOpen, setIsShareVisibilityOpen] = useState(false);
  const [isApplyBrandingOpen, setIsApplyBrandingOpen] = useState(false);
  const [isClearContentOpen, setIsClearContentOpen] = useState(false);
  const [isDeleteContentOpen, setIsDeleteContentOpen] = useState(false);
  const [isResetContentOpen, setIsResetContentOpen] = useState(false);
  const [editorModalStoryContent, setEditorModalStoryContent] = useState<IStoryContent | null>(null);
  const [addMediaState, setAddMediaState] = useState<{ open: boolean; type: 'image' | 'video' | 'background'; storyContent: IStoryContent | null }>({ open: false, type: 'image', storyContent: null });
  const [addEmbedStoryContent, setAddEmbedStoryContent] = useState<IStoryContent | null>(null);
  const [isAddContentOpen, setIsAddContentOpen] = useState(false);
  const [isExportPdfOptionsOpen, setIsExportPdfOptionsOpen] = useState(false);
  const [isPdfLoadingOpen, setIsPdfLoadingOpen] = useState(false);
  // After deleting a standalone story/template, return to the Publications
  // landing (main's default selected tab), not the Stories list — the latter
  // route is rejected by the publications-page load gate.
  const deleteActiveTab = TAB_PUBLICATIONS;
  // This should be happening during state initialization based on the record..
  const storyCustomColors = useMemo(() => initializeCustomColors(story), [story]);
  const { isAdmin, canView, canEdit, canPublish, canDelete, canExport } = useMemo(() => {
    const allBaseChecksPassed =
      entityId &&
      userId &&
      story?.author?.og_id &&
      story.entity_id &&
      (currentSubjectPublicationStoryClaim || currentSubjectClaims);
    const accessChecker = publication?.id
      ? (privilege: IStoryPrivilege) =>
        hasSubjectStoryClaim(currentSubjectClaims, currentSubjectPublicationStoryClaim, privilege)
      : (privilege: IStoryPrivilege) => hasPrivilege(currentSubjectClaims, privilege);

    if (allBaseChecksPassed) {
      const isAdmin = accessChecker(STORIES_ADMIN_PRIVILEGE);

      if (isAdmin) {
        return {
          isAdmin: true,
          canView: true,
          canEdit: true,
          canPublish: true,
          canDelete: true,
          canExport: true
        };
      }

      const hasEditPrivilege = accessChecker(STORIES_EDITOR_PRIVILEGE);

      return {
        isAdmin: false,
        canView: accessChecker(STORIES_VIEWER_PRIVILEGE),
        canEdit: hasEditPrivilege,
        canPublish: accessChecker(STORIES_PUBLISHER_PRIVILEGE),
        canDelete: hasEditPrivilege,
        canExport: accessChecker(STORIES_PDF_GENERATION_PRIVILEGE)
      };
    }

    return {
      isAdmin: false,
      canView: false,
      canEdit: false,
      canPublish: false,
      canDelete: false,
      canExport: false
    };
  }, [entityId, userId, story, currentSubjectClaims, currentSubjectPublicationStoryClaim, storiesAccessControlEnabled, publication]);

  const updateStoryFromSettings = useCallback(
    (partial: Partial<IUpdateStoryProps>) => {
      doUpdateStory({
        story: partial.story,
        isTemplate: partial.isTemplate ?? isTemplate,
        isAdmin: partial.isAdmin ?? isAdmin,
        hasGallery: !!hasGallery,
        shouldRedirect: false,
        entitySubdomain: props.entitySubdomain,
        userId,
        storyContents,
        settings: settings,
        publicationId: publication?.id || '',
        currentSubjectPublicationStoryClaim: partial.currentSubjectPublicationStoryClaim,
        storiesAccessControlEnabled: partial.storiesAccessControlEnabled ?? storiesAccessControlEnabled,
      } as IUpdateStoryProps);
    },
    [doUpdateStory, isTemplate, isAdmin, hasGallery, props.entitySubdomain, userId, storyContents, settings, publication?.id, storiesAccessControlEnabled]
  );

  const updateAllContentsFromSettings = useCallback(
    (p: { isTemplate: boolean; story: IStory; storyContents: IStoryContent[]; entitySubdomain: string }) => {
      doUpdateAllStoryContentsDB({ isTemplate: p.isTemplate, story: p.story, storyContents: p.storyContents });
    },
    [doUpdateAllStoryContentsDB]
  );

  const updateVisibility = useCallback(
    async (p: {
      hasGallery: boolean;
      entitySubdomain: string;
      userId: number;
      isAdmin: boolean;
      isTemplate: boolean;
      story: IStory;
      shareVisibility: 'draft' | 'internalPublished' | 'published';
      currentSubjectPublicationStoryClaim: IExtendedStoryPrivilege | null;
      storiesAccessControlEnabled: boolean;
    }): Promise<void> => {
      if (p.shareVisibility === 'draft' && p.story.state !== 'draft') {
        doUnpublishStory({
          isAdmin: p.isAdmin,
          isTemplate: p.isTemplate,
          entitySubdomain: p.entitySubdomain,
          story: p.story,
          redirectToEdit: false,
          currentSubjectPublicationStoryClaim: p.currentSubjectPublicationStoryClaim,
          storiesAccessControlEnabled: p.storiesAccessControlEnabled,
        });
      } else if (p.shareVisibility === 'internalPublished' || p.shareVisibility === 'published') {
        doPublishStory({
          isTemplate: p.isTemplate,
          hasGallery: p.hasGallery,
          isAdmin: p.isAdmin,
          currentSubjectPublicationStoryClaim: p.currentSubjectPublicationStoryClaim,
          userId: p.userId,
          entitySubdomain: p.entitySubdomain,
          entityUuid,
          story: p.story,
          isInternal: p.shareVisibility === 'internalPublished',
          storiesAccessControlEnabled: p.storiesAccessControlEnabled,
        });
      }
    },
    [doUnpublishStory, doPublishStory, entityUuid]
  );

  const handleExport = useCallback((exportProps: IExportStoryProps) => {
    setIsExportPdfOptionsOpen(false);
    setIsPdfLoadingOpen(true);
    doExportStory(exportProps);
  }, [doExportStory]);

  const handleApplyBranding = useCallback(() => {
    if (publication?.id) {
      void updatePublicationUpdatedAt({ publicationId: publication.id, userId });
    }
    doUpdateAllStoryContentsDB(
      { isTemplate, story, storyContents, settings: settings, entityName, shouldApplyBranding: true },
      {
        onSuccess: () => {
          void storyQuery.refetch();
          if (storyContentsQueryParams) {
            void queryClient.invalidateQueries({ queryKey: storyViewQueryKeys.storyContents(storyContentsQueryParams) });
          }
          toggleToast({ title: props.modalMessages.applyBrandingSuccessful, severity: 'success' });
        }
      }
    );
    setIsApplyBrandingOpen(false);
  }, [doUpdateAllStoryContentsDB, updatePublicationUpdatedAt, isTemplate, story, storyContents, settings, entityName, publication, userId, storyQuery, storyContentsQueryParams, queryClient, props.modalMessages, toggleToast]);

  const handleClearContent = useCallback(() => {
    if (!editorModalStoryContent) return;
    updateStoryContent({ ...editorModalStoryContent, shouldClear: true });
    setIsClearContentOpen(false);
  }, [editorModalStoryContent, updateStoryContent]);

  const handleResetContent = useCallback(() => {
    if (!editorModalStoryContent) return;
    const { title, type, active, format } = editorModalStoryContent;
    const newStoryContents = selectNewStoryContents();
    const newStoryContentTemplate = newStoryContents.find((s) => s.format === format);
    const content =
      type === HEADER && newStoryContentTemplate.content.indexOf('LOGOTYPE') > -1 && entityName
        ? updateLogo(newStoryContentTemplate, entityName.toUpperCase()).content
        : newStoryContentTemplate.content;
    updateStoryContent({
      ...editorModalStoryContent,
      title,
      active,
      content,
      shouldUpdate: true
    });
    setIsResetContentOpen(false);
  }, [editorModalStoryContent, entityName, updateStoryContent]);

  const handleDeleteContent = useCallback(() => {
    if (!editorModalStoryContent) return;
    const embedIds: string[] = [];
    const embeds = editorModalStoryContent.content.match(/data-embed-id="(.*?)"/g);
    if (Array.isArray(embeds)) {
      embeds.forEach((embed) => {
        const embedData = embed.match(/data-embed-id="(.*?)"/);
        if (Array.isArray(embedData) && !!embedData[1] && !embedIds.includes(embedData[1])) {
          embedIds.push(embedData[1]);
        }
      });
    }
    if (publication?.id) {
      void updatePublicationUpdatedAt({ publicationId: publication.id, userId });
    }
    doDeleteStoryContent({
      isTemplate,
      entityId,
      entitySubdomain: props.entitySubdomain,
      story,
      storyContentId: editorModalStoryContent._id,
      embedIds
    });
    if (editorModalStoryContent.content.indexOf('data-embed-id') > -1) {
      const match = editorModalStoryContent.content.match('data-embed-id="([^"]*)"');
      if (match?.[1]) {
        const newEmbeds = (story.embeds ?? []).filter((embed) => embed.id !== match[1]);
        doUpdateStory({
          isTemplate,
          isAdmin,
          hasGallery: !!hasGallery,
          shouldRedirect: false,
          entitySubdomain: props.entitySubdomain,
          settings: settings,
          story: { ...story, embeds: newEmbeds },
          storyContents
        });
      }
    }
    setIsDeleteContentOpen(false);
  }, [editorModalStoryContent, publication, updatePublicationUpdatedAt, userId, doDeleteStoryContent, isTemplate, entityId, props.entitySubdomain, story, doUpdateStory, isAdmin, hasGallery, settings, storyContents]);

  const editorModalContextValue = useMemo(() => ({
    openClearContent: (sc: IStoryContent) => { setEditorModalStoryContent(sc); setIsClearContentOpen(true); },
    openDeleteContent: (sc: IStoryContent) => { setEditorModalStoryContent(sc); setIsDeleteContentOpen(true); },
    openResetContent: (sc: IStoryContent) => { setEditorModalStoryContent(sc); setIsResetContentOpen(true); },
    openAddBackground: (sc: IStoryContent) => setAddMediaState({ open: true, type: 'background', storyContent: sc }),
    openAddImage: (sc: IStoryContent) => setAddMediaState({ open: true, type: 'image', storyContent: sc }),
    openAddVideo: (sc: IStoryContent) => setAddMediaState({ open: true, type: 'video', storyContent: sc }),
    openAddEmbed: (sc: IStoryContent) => setAddEmbedStoryContent(sc),
    openAddContent: () => setIsAddContentOpen(true),
  }), []);

  const { data: session } = useGetSession();
  const userEmail = session?.email;

  useEffect(() => {
    if (!session || !entitySubdomain) return;
    getFlags({
      session: {
        uuid: session.uuid,
        full_name: session.full_name,
        first_name: session.first_name,
        last_name: session.last_name,
        email: session.email,
        entity: { id: session.entity.id },
        role: { name: session.role.name }
      },
      currentEntityId: entitySubdomain
    });
  }, [session?.uuid, entitySubdomain]);
  const [sidePanelConfig, setSidePanelConfig] = useState<WorkspaceSidebarTabsSection[]>([]);
  // The two sidebars rendered by <Sidebar /> persist their collapsed state separately, per user.
  const storyOverviewSidebar = useSidebarCollapsedState(STORY_OVERVIEW_SIDEBAR_STORAGE_KEY_PREFIX, userId);
  const pageLayoutProposalsSidebar = useSidebarCollapsedState(
    STORY_PAGE_LAYOUT_PROPOSALS_SIDEBAR_STORAGE_KEY_PREFIX,
    userId
  );
  const activeSidebar = isReadOnly ? storyOverviewSidebar : pageLayoutProposalsSidebar;
  const deleteCommentHandler = useCallback(
    async ({ comment, reason }: { comment: Comment; reason?: string }): Promise<DeleteCommentResponse> => {
      try {
        const result: DeleteCommentResponse = await transport
          .url(`/api/stories/v1/comments/delete`)
          .json({ commentId: comment.id })
          .post()
          .json()
          .then((res: DeleteCommentResponse) => {
            if (res.errors) return Promise.reject(res.errors);
            return res;
          });
        if (result.success) {
          const commentRemovedActivityData: CommentDeletedActivityData = {
            comment: {
              id: comment.id,
              text: comment.text,
              topicId: story._id,
              topicType: TOPIC_TYPE_STORY,
              entityId: entityUuid,
              removalReason: reason,
              removedBy: userUuid
            },
            userUuid,
            entityId
          };
          void sendCommentRemovedActivity(commentRemovedActivityData);
        }
        return result;
      } catch (error) {
        console.error(error);
        return { success: false, errors: [error] } as unknown as DeleteCommentResponse;
      }
    },
    []
  );
  //this effect is used to add the comments tab to the side panel
  useEffect(() => {
    if (story._id && story.isFollowable) {
      const functionArgs: FunctionArgsType = {
        commonArgs: {
          entityId: entityUuid,
          topicId: story._id,
          topicType: TOPIC_TYPE_STORY
        },
        fetchCommentsArgs: {
          limit: 10
        },
        addCommentArgs: {
          name: entityName,
          email: userEmail,
          type: UserType.EntityAdmin
        },
        deleteCommentArgs: {
          commentId: ''
        }
      };
      const storiesSidePanelTabs: WorkspaceSidebarTab[] = [
        {
          id: COMMENTS_TAB_ID,
          tooltip: COMMENTS_TAB_TITLE,
          icon: <CommentTextOutline />,
          panelTitle: COMMENTS_TAB_TITLE,
          panelContent: (
            <CommentsWidget
              maxCommentLength={280}
              fetchComments={({ limit, offset, parentId }: ArgsType) =>
                fetchComments(
                  { ...functionArgs.fetchCommentsArgs, ...functionArgs.commonArgs, limit, offset, parentId },
                  OpenGovAppConfig.STORIES_COMMUNITY_API_URL
                )
              }
              addComment={({ text, parentId }: ArgsType) =>
                addComment(
                  { ...functionArgs.addCommentArgs, ...functionArgs.commonArgs, text, parentId },
                  OpenGovAppConfig.STORIES_COMMUNITY_API_URL
                )
              }
              deleteComment={({ comment, reason }: { comment: Comment; reason?: string }) =>
                deleteCommentHandler({ comment, reason })
              }
              showRemove={true}
            />
          )
        }
      ];
      const workspaceAdditionalTabsSection = {
        title: SIDE_PANEL_TABS_SECTION_TITLE,
        tabs: storiesSidePanelTabs
      };
      setSidePanelConfig([workspaceAdditionalTabsSection]);
    }
  }, [story._id, story.isFollowable, deleteCommentHandler, entityName, entityUuid, userEmail]);
  // This effect would not be needed if we could
  // add this link tag when the page layout is generated
  useEffect(() => {
    // Inject code for Material Icons into dom
    if (document && document !== void 0) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    return () => {
      // TanStack Query handles cache eviction; no manual reset needed
      if (document && document !== void 0) {
        const head = document.getElementsByTagName('head')[0];
        const link = head.querySelector('link[href$="Material+Icons"]');
        head.removeChild(link);
      }
    };
  }, []);

  // Track story view
  useEffect(() => {
    if (story._id && userUuid && entityId) {
      const timeoutId = setTimeout(() => {
        sendStoriesViewedActivity(story, userUuid, entityId, publication);
      }, 500);

      return () => clearTimeout(timeoutId); // Prevent duplicate call if deps change before timeout
    }
  }, [story._id, publication?.id, userUuid, entityId]);

  useEffect(() => {
    if (entityId && userId && story?.author && story.author.og_id && story.entity_id) {
      // Redirect to publication view if user does not have view permission
      if (isReadOnly && !canView && currentSubjectClaims && publicationStoryClaimSettled) props.navigateToPublicationList();

      // Redirect to View Story if user does not have edit permission
      if (!isReadOnly && !canEdit && currentSubjectClaims && publicationStoryClaimSettled) props.navigateToStoryView();
    }
  }, [entityId, userId, story, isReadOnly, canView, canEdit, props.navigateToStoryView, currentSubjectClaims, publicationStoryClaimSettled]);

  return (
    <EditorModalProvider value={editorModalContextValue}>
      <CurrentEditorProvider>
        <MergeTagsProvider
          mergeTags={mergeTags}
          storyContents={storyContents}
          canAddProposalContent={canAddProposalContent}
          loadingMergeTags={isLoadingProposals}
        >
          <WorkspaceSidebarProvider
            entitySubdomain={props.entitySubdomain}
            entityUuid={props.entityUuid}
            recentActivitiesSource="stories"
            sx={styles.root}
            className="story-view-page"
            customTabsSections={sidePanelConfig}
            formsAndWorkflowsFeatureEnabled={isFormsAndWorkflowsFeatureEnabled}
            performanceManagementFeatureEnabled={isPerformanceManagementFeatureEnabled}
          >
            <Header
              {...props}
              settings={settings}
              isAdmin={isAdmin}
              canEdit={canEdit}
              canDelete={canDelete}
              canExport={canExport}
              onDeleteClick={() => setIsDeleteOpen(true)}
              onCopyStoryClick={() => setIsCopyStoryOpen(true)}
              onCopyToTemplateClick={() => setIsCopyToTemplateOpen(true)}
              onStorySettingsClick={() => setIsStorySettingsOpen(true)}
              onApplyBrandingClick={() => setIsApplyBrandingOpen(true)}
              onExportClick={() => setIsExportPdfOptionsOpen(true)}
              currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim}
              storiesAccessControlEnabled={storiesAccessControlEnabled}
              isSaving={isSaving}
              isPublishing={isPublishing}
              updateStory={updateStory as any}
              publishStory={((publishProps) => publishStory({ ...publishProps, entityUuid, progressTrackerSettings } as any)) as any}
              previewPdf={previewPdf as any}
              refreshEmbeds={refreshEmbeds as any}
              story={story}
              storyContents={storyContents}
              hasGallery={!!hasGallery}
              publication={publication ?? undefined}
              updatePublicationUpdatedAt={updatePublicationUpdatedAt}
              updateThumbnail={updateThumbnail}
            />

            {!isReadOnly ? <MaxNumberOfMergeTagsBanner messages={messages} /> : null}

            <Box sx={styles.wrapper}>
              {!isLoading && (
                <Sidebar
                  {...props}
                  canEdit={canEdit}
                  canPublish={canPublish}
                  isSaving={isSaving}
                  isPublishing={isPublishing}
                  selectedBudget={storyBudget}
                  story={story}
                  storyContents={storyContents}
                  storyMeta={storyMeta}
                  proposalFieldsDetails={proposalFieldsDetails}
                  isLoadingProposals={isLoadingProposals}
                  onRepublishClick={() => setIsRepublishOpen(true)}
                  onShareVisibilityClick={() => setIsShareVisibilityOpen(true)}
                  onClearContent={(sc) => { setEditorModalStoryContent(sc); setIsClearContentOpen(true); }}
                  onResetContent={(sc) => { setEditorModalStoryContent(sc); setIsResetContentOpen(true); }}
                  updateStoryContent={updateStoryContent as any}
                  updateStoryContents={updateStoryContents as any}
                  updateMetaStoryContent={updateMetaStoryContent as any}
                  updateStoryContentDB={updateStoryContentDB as any}
                  updateAllStoryContentsDB={updateAllStoryContentsDB as any}
                  isCollapsed={activeSidebar.isCollapsed}
                  onToggleCollapse={activeSidebar.toggleCollapsed}
                />
              )}
              {isStorySettingsOpen && (
                <StorySettings
                  key={story._id}
                  open={isStorySettingsOpen}
                  onClose={() => setIsStorySettingsOpen(false)}
                  isViewingStoryEditor={!isReadOnly}
                  entitySubdomain={props.entitySubdomain}
                  storyPaddingEnabled={storyPaddingEnabled}
                  isAdmin={isAdmin}
                  isOpenGovAdmin={props.isOpenGovAdmin}
                  story={story}
                  isTemplate={isTemplate}
                  title={story.title ?? ''}
                  description={story.description ?? ''}
                  paddingTop={story.paddingTop}
                  paddingBottom={story.paddingBottom}
                  messages={props.modalMessages}
                  storyContents={storyContents}
                  publication={publication ?? undefined}
                  userId={userId}
                  currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim ?? null}
                  storiesAccessControlEnabled={storiesAccessControlEnabled}
                  updateStory={updateStoryFromSettings}
                  updateAllStoryContentsDB={updateAllContentsFromSettings}
                  updatePublicationUpdatedAt={updatePublicationUpdatedAt}
                />
              )}
              {isShareVisibilityOpen && (
                <ShareVisibilitySettings
                  key={story._id}
                  open={isShareVisibilityOpen}
                  onClose={() => setIsShareVisibilityOpen(false)}
                  entitySubdomain={props.entitySubdomain}
                  hasGallery={!!hasGallery}
                  isAdmin={isAdmin}
                  userId={userId}
                  isTemplate={isTemplate}
                  messages={props.modalMessages}
                  publication={publication ?? undefined}
                  story={story}
                  currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim ?? null}
                  storiesAccessControlEnabled={storiesAccessControlEnabled}
                  canFollowStories={canFollowStories}
                  updateVisibility={updateVisibility}
                  updatePublicationUpdatedAt={updatePublicationUpdatedAt}
                />
              )}
              <ApplyBranding
                open={isApplyBrandingOpen}
                onClose={() => setIsApplyBrandingOpen(false)}
                messages={props.modalMessages}
                handleSubmit={handleApplyBranding}
              />
              <ClearContent
                open={isClearContentOpen}
                onClose={() => setIsClearContentOpen(false)}
                messages={props.modalMessages}
                handleSubmit={handleClearContent}
              />
              <ResetContent
                open={isResetContentOpen}
                onClose={() => setIsResetContentOpen(false)}
                messages={props.modalMessages}
                handleSubmit={handleResetContent}
              />
              <DeleteContent
                open={isDeleteContentOpen}
                onClose={() => setIsDeleteContentOpen(false)}
                messages={props.modalMessages}
                handleSubmit={handleDeleteContent}
              />
              {addMediaState.open && addMediaState.storyContent && (
                <AddMedia
                  isTemplate={props.isTemplate}
                  story={story}
                  entitySubdomain={props.entitySubdomain}
                  mediaType={addMediaState.type}
                  storyContent={addMediaState.storyContent}
                  messages={props.modalMessages}
                  onClose={() => setAddMediaState((s) => ({ ...s, open: false }))}
                  updateStoryContent={updateStoryContent as any}
                  updateStoryContentDB={doUpdateStoryContentDB}
                />
              )}
              {addEmbedStoryContent && (
                <AddEmbed
                  isTemplate={props.isTemplate}
                  story={story}
                  storyContent={addEmbedStoryContent}
                  messages={props.modalMessages}
                  onClose={() => setAddEmbedStoryContent(null)}
                  updateStoryContent={updateStoryContent as any}
                  updateStoryContentDB={doUpdateStoryContentDB as any}
                />
              )}
              {isAddContentOpen && (
                <AddContent
                  isTemplate={props.isTemplate}
                  entityId={entityId}
                  entitySubdomain={props.entitySubdomain}
                  story={story}
                  entityName={entityName}
                  settings={settings}
                  userId={String(userId)}
                  storyContents={storyContents}
                  order={contextStoryContentOrder}
                  groups={selectContentGroups()}
                  newStoryContents={selectNewStoryContents()}
                  messages={props.modalMessages}
                  onClose={() => setIsAddContentOpen(false)}
                  createStoryContent={doCreateStoryContent as any}
                />
              )}
              {isCopyToTemplateOpen && (
                <CopyToTemplate
                  key={story._id}
                  open={isCopyToTemplateOpen}
                  onClose={() => setIsCopyToTemplateOpen(false)}
                  entityId={entityId}
                  entitySubdomain={props.entitySubdomain}
                  userId={userId}
                  title={story.title}
                  description={story.description}
                  paddingTop={story.paddingTop ?? 0}
                  paddingBottom={story.paddingBottom ?? 0}
                  storyContents={storyContents}
                  settings={settings}
                  customCssCode={story.customCssCode}
                  messages={props.modalMessages}
                />
              )}
              <CopyStory
                open={isCopyStoryOpen}
                onClose={() => setIsCopyStoryOpen(false)}
                closeModal={() => setIsCopyStoryOpen(false)}
                copyStory={async () => { }}
                entityId={entityId}
                entitySubdomain={props.entitySubdomain}
                storyId={storyId}
                storyFolderId={story.folderId}
                publicationId={publicationId}
                title={story.title}
                messages={props.modalMessages}
              />
              <DeleteStory
                open={isDeleteOpen}
                onClose={() => setIsDeleteOpen(false)}
                isTemplate={isTemplate}
                story={story}
                publication={publication ?? undefined}
                userId={userId}
                entitySubdomain={props.entitySubdomain}
                activeTab={deleteActiveTab}
                messages={props.modalMessages}
                updatePublicationUpdatedAt={updatePublicationUpdatedAt}
              />
              <RepublishStory
                open={isRepublishOpen}
                onClose={() => setIsRepublishOpen(false)}
                story={story}
                publication={publication ?? undefined}
                userId={userId}
                isAdmin={isAdmin}
                hasGallery={!!hasGallery}
                isTemplate={isTemplate}
                entitySubdomain={props.entitySubdomain}
                isInternal={story.state === 'internalPublished'}
                storiesAccessControlEnabled={storiesAccessControlEnabled}
                currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim}
                messages={props.modalMessages}
                publishStory={((p) => publishStory({ ...p, entityUuid, progressTrackerSettings } as any)) as any}
                updatePublicationUpdatedAt={updatePublicationUpdatedAt}
              />
              <ExportPdfOptions
                open={isExportPdfOptionsOpen}
                onClose={() => setIsExportPdfOptionsOpen(false)}
                onExport={handleExport}
                isAdmin={isAdmin}
                isTemplate={isTemplate}
                storyMarginsEnabled={storyMarginsEnabled}
                hasGallery={!!hasGallery}
                messages={props.modalMessages}
                isPreviewingPdf={isPreviewingPdf}
                isPreviewingPdfFailure={isPreviewingPdfFailure}
                settings={settings}
                story={story}
                publication={publication ?? undefined}
                entitySubdomain={props.entitySubdomain}
                storyContents={storyContents}
                previewPdf={previewPdf as any}
              />
              <PdfLoading
                open={isPdfLoadingOpen}
                onClose={() => setIsPdfLoadingOpen(false)}
                messages={props.modalMessages}
                isExporting={isExporting}
                isExportingFailure={isExportingFailure}
              />
              <ModalProvider>
                <Content
                  {...props}
                  settings={settings}
                  storyTitle="OpenGov Story"
                  storyDescription=""
                  messages={editorMessages}
                  storyCustomColors={storyCustomColors}
                  isReadOnly={isReadOnly}
                  entityId={entityId}
                  isAdmin={isAdmin}
                  isLoading={isLoading}
                  isSettingsLoading={isSettingsLoading}
                  isLoadingProposals={isLoadingProposals}
                  story={story}
                  storyContents={storyContents}
                  publication={publication ?? undefined}
                  inactiveStoryMergeTagsType={inactiveStoryMergeTagsType}
                  updateStoryContent={updateStoryContent as any}
                  updateStoryContents={updateStoryContents as any}
                  updateMetaStoryContent={updateMetaStoryContent as any}
                  updateMetaStoryContentOrder={updateMetaStoryContentOrder as any}
                  updateMetaStoryContentInView={updateMetaStoryContentInView as any}
                  updateStoryCustomColors={updateStoryCustomColors as any}
                  updateStoryContentDB={updateStoryContentDB as any}
                  updateAllStoryContentsDB={updateAllStoryContentsDB as any}
                  updateAllStoryContentsMergeTagsDB={updateAllStoryContentsMergeTagsDB as any}
                  deleteStoryEmbed={deleteStoryEmbed as any}
                  saveProgressTrackerConfig={saveProgressTrackerConfig as any}
                  updatePublicationUpdatedAt={updatePublicationUpdatedAt}
                />
              </ModalProvider>
            </Box>
            <Alert
              effect="scale"
              position="bottom-right"
              timeout={5000}
              offset={25}
              stack={{ limit: 3, spacing: 25 }}
              contentTemplate={Flash}
            />
          </WorkspaceSidebarProvider>
        </MergeTagsProvider>
      </CurrentEditorProvider>
    </EditorModalProvider>
  );
};

export const StoryView = (props: IStoryViewProps) => {
  return (
    <PageErrorBoundary viewName={ViewName.StoryViewPage}>
      <ProgressTrackerSettingsProvider entityId={props.entityId}>
        <StoryContentMetaProvider>
          <StoryViewInternal {...props} />
        </StoryContentMetaProvider>
      </ProgressTrackerSettingsProvider>
    </PageErrorBoundary>
  );
};
